import React from 'react';
import { CircularProgress, FormControlLabel, Button, Typography, Checkbox, IconButton } from '@material-ui/core';
import { makeStyles, type Theme } from '@material-ui/core/styles';
import AddIcon from '@material-ui/icons/Add';
import { colors } from '@skyslope/mache';
import emailValidator from 'email-validator';
import { v4 as uuid } from 'uuid';
import { validateInput } from '@repo/utils';
import { useIsPrimeUser } from '../../../hooks';
import { ReactComponent as PenIcon } from '../../../images/pen.svg';
import { ReactComponent as TrashIcon } from '../../../images/trash.svg';
import { useGetEnvelopeAutoReminders, useUpdateEnvelopeAutoReminders } from '../../../lib/api/envelope';
import { useShallowSelector } from '../../../lib/reduxHooks';
import { applyColorAndCounterToNewRecipient, getRecipientColor } from '../../../lib/utils';
import StyledTooltip from '../../StyledTooltip';
import { chameleonElId, checkIfRecipientIsSender } from '../../utils';
import { type IGroup, type IRecipient, RecipientRole, type IContact } from '../types';
import PlaceholderRecipient from './PlaceholderRecipient';
import Recipient from './Recipient';
import RecipientAddOrEdit from './RecipientAddOrEdit';
import RecipientSearch from './RecipientSearch';

const useStyles = makeStyles((theme: Theme) => ({
  wrapper: {
    padding: '32px 24px',
    overflow: 'auto',
  },
  addIcon: {
    height: 20,
    width: 20,
  },
  recipient: (props: Props) => ({
    display: 'flex',
    alignItems: 'center',
    marginLeft: props.optionalRecipients?.length ? (!props.modal ? 40 : 0) : 0,
  }),
  button: {
    padding: 0,
    color: colors.blue[800],
  },
  iconWrapper: {
    display: 'grid',
    gridGap: theme.spacing(),
    gridAutoFlow: 'column',
    paddingLeft: theme.spacing(2),
  },
  icon: {
    width: 24,
    height: 24,
  },
  buttonIcon: {
    margin: -8,
    padding: 8,
    color: colors.grey[500],
    '&:hover': {
      color: colors.blue[800],
    },
  },
  activeCheckbox: {
    margin: '-8px 8px -8px -48px',
    padding: 8,
    color: colors.blue[800],
  },
  inactiveCheckbox: {
    padding: 8,
  },
  tooltipBox: {
    margin: '-8px 8px -8px -48px',
  },
}));

type Props = {
  envelopeId: string;
  setRecipients: (recipients: IRecipient[]) => void;
  recipients: IRecipient[];
  signingGroups: IGroup[];
  lastGroupEmpty: boolean;
  contacts: any[];
  optionalRecipients?: IRecipient[];
  incompletePlaceholders?: IRecipient[];
  createPrimeContact?: (contact: { firstName: string; lastName: string; email: string }) => void;
  modal?: boolean;
  disableRecipientList?: boolean;
  colorCounter?: number;
  showAutomatedRemindersFlag: boolean;
};

export function RecipientList(props: Props) {
  const {
    envelopeId,
    recipients,
    modal,
    disableRecipientList,
    colorCounter,
    incompletePlaceholders,
    showAutomatedRemindersFlag,
  } = props;
  const isPrimeUser = useIsPrimeUser();

  const classes = useStyles(props);
  const [isAddRecipientFlow, setIsAddRecipientFlow] = React.useState(false);
  const [recipientToEdit, setRecipientToEdit] = React.useState<IRecipient>();
  const [optionalCheckedMap, setOptionalCheckedMap] = React.useState<{ [rId: string]: boolean }>({});
  const [optionalRecipients, setOptionalRecipients] = React.useState<IRecipient[]>();
  const [placeholderRecipients, setPlaceholderRecipients] = React.useState(incompletePlaceholders);

  const enableAutoReminders = useAutoReminders({ envelopeId, showAutomatedRemindersFlag });

  React.useEffect(() => {
    setPlaceholderRecipients(incompletePlaceholders?.sort((a, b) => a.recipientColorCounter - b.recipientColorCounter));
    // This data is loaded inside of an effect in useInitRecipientManagement.
    // It will be undefined when this component first loads. After real data
    // is loaded, we don't want to update the local state in this component
    // again as it could override user input. This will also change if the
    // user goes back and selects a different template.
  }, [incompletePlaceholders]);

  React.useEffect(() => {
    if (recipientToEdit) {
      const updatedRecipientToEdit = recipients.find((r) => r.id === recipientToEdit.id);
      setRecipientToEdit(updatedRecipientToEdit);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- This code was written prior to enforcing lint rules
  }, [recipients]);

  React.useEffect(() => {
    const ors = props.optionalRecipients ? [...props.optionalRecipients] : [];
    ors.forEach((or) => {
      or.group = or.group || props.signingGroups[0];
    });
    props.recipients.forEach((r) => {
      if (!modal) {
        if (r.isPlaceholder && !ors.some((recipient) => recipient.id === r.id) && (r.firstName || r.lastName)) {
          ors.push(r);
        }
      }
    });
    const map = {};
    ors.forEach((or) => {
      for (let i = 0; i < recipients.length; i++) {
        const r = recipients[i];
        if (r.id === or.id) {
          // @ts-expect-error -- This code was written prior to enforcing lint rules
          map[or.id] = true;
          return;
        }
        if (
          r.firstName === or.firstName &&
          r.lastName === or.lastName &&
          r.email === or.email &&
          r.title?.toLowerCase() === or.title?.toLowerCase()
        ) {
          or.id = r.id;
          // @ts-expect-error -- This code was written prior to enforcing lint rules
          map[or.id] = true;
          return;
        }
      }
    });
    // check if sender is in both groups
    setOptionalRecipients(ors);
    setOptionalCheckedMap(map);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- This code was written prior to enforcing lint rules
  }, [props.signingGroups, props.optionalRecipients]);

  function addRecipient() {
    setIsAddRecipientFlow(true);
  }

  function editRecipient(recipient: IRecipient) {
    setIsAddRecipientFlow(true);
    setRecipientToEdit(recipient);
  }

  function cancelEdit() {
    setIsAddRecipientFlow(false);
    setRecipientToEdit(undefined);
  }

  function saveRecipient(recipient: IRecipient, isNew: boolean) {
    recipient.isActive = true;
    const contact = {
      firstName: recipient.firstName,
      lastName: recipient.lastName,
      email: recipient.email,
      optional: recipient.isPlaceholder,
      id: recipient.id,
    };
    if (isPrimeUser) {
      props.createPrimeContact?.(contact);
      const index = recipients.findIndex((r) => r.id === recipient.id);
      if (isNew || index < 0) {
        props.setRecipients([...recipients, recipient]);
      } else {
        const newRecipients = [...recipients];
        newRecipients[index] = recipient;
        props.setRecipients(newRecipients);
      }
    }
    if (optionalRecipients?.some((r) => r.id === recipient.id)) {
      const index = optionalRecipients.findIndex((or) => or.id === recipient.id);
      const newOptionalRecipients = [...optionalRecipients];
      newOptionalRecipients[index] = recipient;
      setOptionalRecipients(newOptionalRecipients);
      if (optionalCheckedMap[recipient.id]) {
        const index = recipients.findIndex((r) => r.id === recipient.id);
        const newRecipients = [...recipients];
        newRecipients[index] = recipient;
        props.setRecipients(newRecipients);
      }
    } else {
      const index = recipients.findIndex((r) => r.id === recipient.id);
      if (isNew || index < 0) {
        props.setRecipients([...recipients, recipient]);
      } else {
        const newRecipients = [...recipients];
        newRecipients[index] = recipient;
        props.setRecipients(newRecipients);
      }
    }

    if (recipient.isPlaceholder && placeholderRecipients?.some((pr) => pr.id === recipient.id)) {
      const foundPlaceholder = placeholderRecipients.find((pr) => pr.id === recipient.id);
      const index = foundPlaceholder ? placeholderRecipients.indexOf(foundPlaceholder) : -1;
      const newPlaceholders = [...placeholderRecipients];
      newPlaceholders.splice(index, 1);
      setPlaceholderRecipients(newPlaceholders);
    }
  }

  function handleSearchResultSelect(contact: IContact) {
    // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
    const isContactValid = (contactParam: IContact) =>
      !validateInput(contactParam.email, 'email') &&
      !validateInput(contactParam.firstName, 'firstname') &&
      !validateInput(contactParam.lastName, 'lastname');

    // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
    const recipient = (contactParam: IContact): IRecipient =>
      ({
        id: uuid(),
        firstName: contactParam.firstName,
        lastName: contactParam.lastName,
        email: contactParam.email,
        role: RecipientRole.Signer,
        group: {
          id: 'default',
          name: 'default',
          order: 0,
        },
        isActive: true,
      }) as IRecipient;

    // If contact is undefined or null, do nothing
    if (!contact) return;
    // If all fields are valid, call saveRecipient
    if (isContactValid(contact)) {
      saveRecipient(recipient(contact), true);
    } else {
      // If not, go to recipient add or edit with that recipient
      setIsAddRecipientFlow(true);
      setRecipientToEdit(recipient(contact));
    }
  }

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const deleteRecipient = (recipient: IRecipient) => {
    props.setRecipients(recipients.filter((item) => item.id !== recipient.id));
  };

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const toggleOptionalRecipient = (optionalRecipient: IRecipient) => {
    const checked = !optionalRecipient.isActive;
    setOptionalCheckedMap({ ...optionalCheckedMap, [optionalRecipient.id]: checked });
    const updatedOptionalRecipients: IRecipient[] = [...(optionalRecipients ?? [])];
    const indexToUpdate = optionalRecipients?.findIndex((op) => op.id === optionalRecipient.id) ?? -1;
    if (checked) {
      if (optionalRecipient.recipientColorCounter) {
        if (indexToUpdate >= 0) {
          updatedOptionalRecipients[indexToUpdate].isActive = true;
          updatedOptionalRecipients[indexToUpdate].color = getRecipientColor(optionalRecipient.recipientColorCounter);
        }
        setOptionalRecipients(updatedOptionalRecipients);
      }
      optionalRecipient.isActive = true;
      optionalRecipient.group = {
        id: 'default',
        name: 'Group 1',
        order: 0,
      };
      let newList = [...recipients, optionalRecipient];
      applyColorAndCounterToNewRecipient(newList, recipients, colorCounter);
      newList = newList.filter((r, index) => newList.indexOf(r) === index);
      props.setRecipients(newList);
    } else {
      const recipient = recipients.find((r) => r.id === optionalRecipient.id);
      if (recipient) recipient.isActive = false;
      const r = recipients.filter((r) => r.isPlaceholder || r.isActive);
      props.setRecipients(r);
      if (indexToUpdate >= 0) {
        updatedOptionalRecipients[indexToUpdate].isActive = false;
        updatedOptionalRecipients[indexToUpdate].color =
          recipient?.title === 'Me' ? colors.blue[800] : colors.grey[600];
      }
      if (!recipient?.isPlaceholder) {
        // @ts-expect-error -- This code was written prior to enforcing lint rules
        delete updatedOptionalRecipients[indexToUpdate].recipientColorCounter;
      }
      setOptionalRecipients(updatedOptionalRecipients);
    }
  };

  if (disableRecipientList) return null;

  if (isAddRecipientFlow) {
    return (
      <div className="py-8 px-6">
        <Typography variant="h6">Recipients</Typography>
        <RecipientAddOrEdit
          recipient={recipientToEdit}
          signingGroups={props.signingGroups}
          recipients={props.recipients}
          saveRecipient={saveRecipient}
          returnToRecipientList={cancelEdit}
          lastGroupEmpty={props.lastGroupEmpty}
          contacts={props.contacts}
          modal={!!modal}
        />
      </div>
    );
  }

  return (
    <div className="pt-8 pb-4 px-6 flex flex-col">
      <Typography variant="h6">Recipients</Typography>

      {isPrimeUser ? (
        <div id={chameleonElId(26)}>
          <RecipientSearch
            autoFocus
            helperText=""
            handleOnChange={(_e) => {}}
            value=""
            contacts={props.contacts}
            dataSpec="recipient-search"
            handleContactSelect={handleSearchResultSelect}
            addRecipientNode={
              <div>
                Sorry, we couldn&apos;t find them. Please try again or
                <Button
                  color="primary"
                  onClick={addRecipient}
                  data-spec="add-recipient-btn"
                  className="bg-transparent pl-0"
                >
                  click here to add a new recipient.
                </Button>
              </div>
            }
          />
        </div>
      ) : null}

      <div className="grid gap-6 mt-8">
        {placeholderRecipients && placeholderRecipients.length && !modal ? (
          <PlaceholderRecipient placeholderRecipients={placeholderRecipients} editRecipient={editRecipient} />
        ) : null}
        {optionalRecipients
          ?.filter((or) => (modal ? or.isActive : true))
          .map((or, index) => (
            <OptionalRecipient
              allRecipients={recipients}
              firstRecipient={index === 0}
              recipient={or}
              key={or.id}
              classes={classes}
              toggleOptionalRecipient={toggleOptionalRecipient}
              editRecipient={editRecipient}
              modal={!!modal}
            />
          ))}
        {recipients
          .filter((r) => !(r.id in optionalCheckedMap) && !isUserPlaceholder(r) && (modal ? r.isActive : true))
          .map((r) => {
            return (
              <div className={classes.recipient} key={r.id}>
                <Recipient firstName={r.firstName} lastName={r.lastName} color={r.color ?? ''} title={r.title} />

                <div className={classes.iconWrapper}>
                  {r.isPlaceholder ? null : (
                    <IconButton color="primary" onClick={() => deleteRecipient(r)} className={classes.buttonIcon}>
                      <span className="sr-only">Delete Recipient</span>
                      <TrashIcon className={classes.icon} />
                    </IconButton>
                  )}

                  <IconButton color="primary" onClick={() => editRecipient(r)} className={classes.buttonIcon}>
                    <span className="sr-only">Edit Recipient</span>
                    <PenIcon className={classes.icon} />
                  </IconButton>
                </div>
              </div>
            );
          })}
      </div>

      {recipients.length < 25 ? (
        <div className="mt-8 w-full text-left">
          <Button
            color="primary"
            onClick={addRecipient}
            data-spec="add-recipient-btn"
            aria-label="add new recipient"
            startIcon={<AddIcon />}
          >
            New Recipient
          </Button>
        </div>
      ) : null}

      {showAutomatedRemindersFlag && (
        <FormControlLabel
          className="mt-auto pt-8"
          control={
            enableAutoReminders.isLoading ? (
              <CircularProgress size={20} className="mr-1 mt-0 p-[10px]" />
            ) : (
              <Checkbox
                checked={enableAutoReminders.data}
                onChange={(e) => enableAutoReminders.mutate(e.target.checked)}
                name="enableAutoReminders"
                color="primary"
              />
            )
          }
          label="Enable automatic reminders on this envelope."
        />
      )}
    </div>
  );
}

function isUserPlaceholder(recipient: IRecipient): boolean {
  return Boolean(recipient.isPlaceholder && (recipient.firstName === null || recipient.email === null));
}

function useAutoReminders(args: { envelopeId: string; showAutomatedRemindersFlag: boolean }) {
  const { envelopeId, showAutomatedRemindersFlag } = args;

  const { settings, settingsFetched } = useShallowSelector((state) => ({
    settings: state.pageFrame.settings,
    settingsFetched: state.pageFrame.settingsFetched,
  }));

  const getAutoReminders = useGetEnvelopeAutoReminders(envelopeId);
  const updateAutoReminders = useUpdateEnvelopeAutoReminders(envelopeId);
  const defaultEnabled = showAutomatedRemindersFlag ? (settings?.user.automatedReminders?.enabled ?? true) : false;

  // Initialize the auto reminders for this envelope based on the user's settings
  // if it's not already set.
  React.useEffect(() => {
    if (!settingsFetched || getAutoReminders.isLoading) return;
    if (getAutoReminders.data != null) return;

    updateAutoReminders.mutate({ automatedRemindersEnabled: defaultEnabled });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- This code was written prior to enforcing lint rules
  }, [settingsFetched, getAutoReminders.isLoading, showAutomatedRemindersFlag]);

  return {
    isLoading: getAutoReminders.isLoading,
    data: getAutoReminders.data ?? defaultEnabled,
    mutate: (automatedRemindersEnabled: boolean) => updateAutoReminders.mutate({ automatedRemindersEnabled }),
  };
}

function OptionalRecipient(props: {
  recipient: IRecipient;
  allRecipients: IRecipient[];
  toggleOptionalRecipient: any;
  editRecipient: any;
  classes: any;
  firstRecipient: boolean;
  modal: boolean;
}) {
  const { classes, toggleOptionalRecipient, editRecipient, firstRecipient, modal, allRecipients } = props;
  const or = allRecipients.find((recipient) => recipient.id === props.recipient.id) || props.recipient;
  const validRecipient = emailValidator.validate(or.email) && or.firstName?.trim().length > 0;
  return (
    <div className={classes.recipient} key={or.id}>
      {modal ? null : (
        <>
          {' '}
          {validRecipient ? (
            <Checkbox
              color="primary"
              className={classes.activeCheckbox}
              checked={or.isActive}
              onClick={() => toggleOptionalRecipient(or)}
              id={firstRecipient ? chameleonElId(13) : ''}
            />
          ) : (
            <StyledTooltip title="You must edit this recipient before adding them" arrow>
              <span className={classes.tooltipBox}>
                <Checkbox color="primary" className={classes.inactiveCheckbox} disabled />
              </span>
            </StyledTooltip>
          )}
        </>
      )}
      <Recipient
        firstName={or.firstName}
        lastName={or.lastName}
        color={or.color ?? ''}
        isSender={checkIfRecipientIsSender(or)}
        title={or.title}
      />
      <div className={classes.iconWrapper}>
        <IconButton
          color="primary"
          onClick={() => editRecipient(or)}
          aria-label="edit recipient"
          className={classes.buttonIcon}
          id={firstRecipient ? chameleonElId(14) : ''}
        >
          <PenIcon className={classes.icon} />
        </IconButton>
      </div>
    </div>
  );
}
